import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, Link as MuiLink, Stack, Typography } from '@mui/material';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ComputerIcon from '@mui/icons-material/Computer';
import SmartToyIcon from '@mui/icons-material/SmartToyOutlined';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMicOutlined';
import { supportAgentApi, ChatDoc, ChatListItem } from './support-agent-api';
import { RatingsStack } from './rating-stars';
import {
  CustomerLogin,
  CustomerMatch,
  chatOutcome,
  chatRatings,
  groupByCustomer,
  initialsFor,
  listItemFromDoc,
  loginChannels,
  shortChatDate,
} from './chat-queue';

// How many of a person's chats Past chats and the history load: plenty for
// one person, one request.
export const CUSTOMER_CHATS_LIMIT = 50;


/** A chat of this login's: app and web logins are different users, so match the channel too. */
const fromLogin = (item: ChatListItem, login: CustomerLogin) =>
  item.userId === login.userId && (login.channel === 'app' ? item.channel === 'app' : item.channel !== 'app');

/**
 * One login's chats, newest first (pom-2-api #604's `userId` filter), up
 * to CUSTOMER_CHATS_LIMIT, and how many it has in all — said, never
 * silently cut.
 */
async function loadLoginChats(login: CustomerLogin): Promise<{ items: ChatListItem[]; total: number }> {
  const res = await Promise.resolve().then(() => supportAgentApi.listChats({ userId: login.userId, limit: CUSTOMER_CHATS_LIMIT }));
  const items = (res?.items ?? []).filter((item) => fromLogin(item, login));
  return { items, total: Math.max(typeof res?.total === 'number' ? res.total : 0, items.length) };
}

/**
 * A person's chats from every login they have, newest first, and how many
 * in all. A login that fails to load is left out (`failed`), never the
 * whole list.
 */
export async function loadPersonChats(
  person: Pick<CustomerMatch, 'logins'>,
): Promise<{ items: ChatListItem[]; total: number; failed: boolean }> {
  const results = await Promise.allSettled(person.logins.map(loadLoginChats));
  const seen = new Set<string>();
  const items: ChatListItem[] = [];
  let total = 0;
  let failed = false;
  results.forEach((result) => {
    if (result.status === 'rejected') {
      failed = true;
      return;
    }
    total += result.value.total;
    result.value.items.forEach((item) => {
      if (seen.has(item.chatId)) return;
      seen.add(item.chatId);
      items.push(item);
    });
  });
  if (failed && items.length === 0) throw new Error('Could not load this person\'s chats');
  const time = (c: ChatListItem) => new Date(c.createdAt).getTime();
  items.sort((a, b) => time(b) - time(a));
  return { items, total: Math.max(total, items.length), failed };
}

/**
 * Who a chat is with, across their logins: the chat's own login, merged
 * with any other login that shares its email or phone (found by searching
 * for them). Falls back to the chat's own login when a search fails.
 */
export async function findPersonFor(chat: ChatDoc): Promise<CustomerMatch> {
  const own = listItemFromDoc(chat);
  const lookups = [chat.userEmail, chat.userPhoneNumber]
    .filter((value): value is string => !!value && value.trim().length >= 2)
    .map((search) =>
      Promise.resolve()
        .then(() => supportAgentApi.listChats({ search, limit: CUSTOMER_CHATS_LIMIT }))
        .then((res) => res?.items ?? [])
        .catch(() => [] as ChatListItem[]),
    );
  const found = (await Promise.all(lookups)).flat();
  const people = groupByCustomer([own, ...found]);
  const ownKey = `${own.channel === 'app' ? 'app' : 'web'}:${own.userId}`;
  return people.find((p) => p.logins.some((login) => `${login.channel}:${login.userId}` === ownKey)) ?? people[0];
}

/**
 * Where a chat came from, or where a person chats from, as an icon
 * (Michael, 10/23): a phone for the POM app, a computer for the Safety Hub
 * website. Read out as "App" / "Web".
 */
export function ChannelIcon({ channel, size = 16, testId }: { channel: 'app' | 'web'; size?: number; testId?: string }) {
  const app = channel === 'app';
  const Icon = app ? SmartphoneIcon : ComputerIcon;
  return (
    <Box
      component="span"
      role="img"
      aria-label={app ? 'App' : 'Web'}
      title={app ? 'From the POM app' : 'From the Safety Hub website'}
      data-testid={testId}
      // App in the darkest brand blue, web in the lighter one (Michael, 10/23), so the two read apart at a glance.
      sx={{ display: 'inline-flex', alignItems: 'center', flex: '0 0 auto', color: app ? 'primary.darker' : 'primary.main' }}
    >
      <Icon sx={{ fontSize: size }} />
    </Box>
  );
}

/**
 * Who handled a chat, as an icon (Michael, 10/23): a robot when the
 * assistant handled it alone, a headset once it was escalated to support.
 * Assistant blue; support dark gray (Michael, 10/23: not amber).
 */
export function HandlerIcon({ by, size = 16, testId }: { by: 'assistant' | 'support'; size?: number; testId?: string }) {
  const support = by === 'support';
  const Icon = support ? HeadsetMicIcon : SmartToyIcon;
  return (
    <Box
      component="span"
      role="img"
      aria-label={support ? 'Escalated to support' : 'Assistant only'}
      title={support ? 'Escalated to support' : 'The assistant handled it alone'}
      data-testid={testId}
      sx={{ display: 'inline-flex', alignItems: 'center', flex: '0 0 auto', color: support ? '#454F5B' : '#0B5A86' }}
    >
      <Icon sx={{ fontSize: size }} />
    </Box>
  );
}

/**
 * Past chats, in the open chat's details pane (the split view design,
 * Michael 10/21): this person's other chats, newest first. Picking one
 * shows all their chats in the inbox's list with that one open.
 */
export function PastChats({ chat, onPick }: { chat: ChatDoc; onPick: (person: CustomerMatch, item?: ChatListItem) => void }) {
  const [items, setItems] = useState<ChatListItem[] | null>(null);
  const [person, setPerson] = useState<CustomerMatch | null>(null);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(false);
  // Some of their logins didn't load: the list is partial, and says so.
  const [partial, setPartial] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setPerson(null);
    setFailed(false);
    setPartial(false);
    (async () => {
      // Every login this person chats from (Michael, 10/23), then their chats.
      const who = await findPersonFor(chat);
      const found = await loadPersonChats(who);
      if (cancelled) return;
      const others = found.items.filter((item) => item.chatId !== chat._id);
      setPerson(who);
      setItems(others);
      // Everyone else's chats count, not just the ones loaded.
      setPartial(found.failed);
      // The open chat is in the total whenever its own login loaded — every
      // login did unless `failed`, even when it isn't among the newest loaded
      // (attack/defend 10/26: the count read one too high).
      const includesOpen = !found.failed || found.items.length > others.length;
      setTotal(Math.max(others.length, found.total - (includesOpen ? 1 : 0)));
    })().catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat._id, chat.userId]);

  return (
    <Box component="details" data-testid="chat-past-chats" sx={{ mt: 2.5, '& > summary': { cursor: 'pointer' } }}>
      <Typography
        component="summary"
        variant="overline"
        sx={{ color: 'text.secondary', fontWeight: 700, lineHeight: 2 }}
      >
        Past chats · {items ? total : '…'}
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {failed && (
          <Typography variant="caption" color="text.secondary">
            Couldn't load past chats.
          </Typography>
        )}
        {partial && (
          <Typography variant="caption" color="text.secondary" data-testid="chat-past-chats-partial">
            Couldn't load all of their past chats.
          </Typography>
        )}
        {items && items.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No earlier chats.
          </Typography>
        )}
        {person &&
          items?.map((item) => (
            <ButtonBase
              key={item.chatId}
              onClick={() => onPick(person, item)}
              data-testid="chat-past-chat"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                width: '100%',
                p: 1,
                borderRadius: 1.5,
                fontFamily: 'inherit',
                '&:hover': { bgcolor: 'grey.100' },
                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.dark', outlineOffset: 2 },
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                  {shortChatDate(item.createdAt)} · {chatOutcome(item)}
                </Typography>
                {person && person.logins.length > 1 && <ChannelIcon channel={item.channel === 'app' ? 'app' : 'web'} size={14} />}
              </Stack>
              <Typography
                component="span"
                variant="body2"
                sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {item.firstMessage || '(no message)'}
              </Typography>
              {(() => {
                const ratings = chatRatings(item);
                return ratings ? <RatingsStack assistant={ratings.assistant} support={ratings.support} /> : null;
              })()}
            </ButtonBase>
          ))}
        {items && total > items.length && person && (
          <Typography variant="caption" color="text.secondary">
            The newest {items.length} are here.{' '}
            <MuiLink component="button" type="button" onClick={() => onPick(person)} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>
              See all their chats
            </MuiLink>
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Whose chats the inbox list is showing (Find a customer, Past chats;
 * Michael 10/23: the same split view, so a chat reads, replies and labels
 * the same as from the queue). Above the person's chats in the list column.
 */
export function CustomerSummary({
  person,
  total,
  shown,
  onBack,
}: {
  person: CustomerMatch;
  // How many chats they have in all, and how many the list holds.
  total: number | null;
  shown: number;
  onBack: () => void;
}) {
  return (
    <Box data-testid="customer-summary" sx={{ mt: 1.25 }}>
      <Button onClick={onBack} size="small" sx={{ textTransform: 'none', fontWeight: 600, px: 0.5, mb: 1 }}>
        ← Back to the queue
      </Button>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#E6F6FC',
            color: '#0B5A86',
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
          }}
        >
          {initialsFor(person.label)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {person.label}
            </Typography>
            {loginChannels(person).map((channel) => (
              <ChannelIcon key={channel} channel={channel} size={18} />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
            {[person.contact, total === null ? '' : total === 1 ? '1 chat' : `${total} chats`]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
      </Stack>
      {total !== null && total > shown && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} data-testid="customer-summary-cut">
          The newest {shown} are listed.
        </Typography>
      )}
    </Box>
  );
}
